import { gql, useLazyQuery, useQuery } from '@apollo/client';
import {
  Currencies,
  FeatureFlagTypes,
  Frequency,
  type WorkflowCompletion,
} from '@generated/graphql';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  AccountTypes,
  CountryCodes,
  type CurrencyCodes,
} from '@onevesthq/ov-enums';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AccountTypeSelect } from '@ovComponents/3-pattern/accountTypeSelect/accountTypeSelect';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import { BankAccountSelectField } from '@ovComponents/3-pattern/bankAccountSelectField/bankAccountSelect';
import { BankSelectField } from '@ovComponents/3-pattern/bankSelectField/bankSelect';
import { HoldingSelectField } from '@ovComponents/3-pattern/holdingSelectField/holdingSelect';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import {
  type ValidateTransferResponse,
  validateTransfer,
} from '@ovComponents/accountRestrictions';
import { TransferErrorBanner } from '@ovComponents/transferErrorBanner';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { UserContext } from '@providers/userContextProvider';
import {
  addBusinessDays,
  isBusinessDayAndAllowWeekend,
  isBusinessDayAndAllowWeekendSync,
  isBusinessDaySync,
} from '@utils/businessDays';
import { DEFAULT_SUPPORT_URL } from '@utils/constants';
import { currencySymbol, getCurrencyFlag } from '@utils/currency';
import { dollars2cents } from '@utils/dollars2cents';
import { FlagsmithFlags } from '@utils/flagsmith';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { objectFilter } from '@utils/objectFilter';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Alert,
  Box,
  Stack,
  Typography,
  useSnackbar,
  useTheme,
} from 'hephaestus/web';
import { Info as InfoIcon } from 'hephaestus/webIcons';
import { isNull, isUndefined } from 'lodash-es';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import { invalidFields } from '../utils';
import { useDepositWorkflowData } from '../utils/useDepositWorkflowData';

const LOCKED_IN_ACCOUNTS = [
  AccountTypes.LIRA,
  AccountTypes.LRSP,
  AccountTypes.RLSP,
];
const INCOME_FUND_ACCOUNTS = [
  AccountTypes.RRIF,
  AccountTypes.RIF_SPOUSAL,
  AccountTypes.LIF,
  AccountTypes.PRIF,
  AccountTypes.LRIF,
  AccountTypes.RLIF,
];

const ACCOUNTS_FOR_TRANSFER_TO_FHSA = [
  AccountTypes.PERSONAL,
  AccountTypes.RRSP,
  AccountTypes.FHSA,
];
const ACCOUNTS_FOR_TRANSFER_FROM_FHSA = [AccountTypes.RRSP, AccountTypes.FHSA];

type DepositAccountConfigInterface = {
  [key: string]: { endOfPriorFiscalYear: Dayjs };
};
const iraEndOfPriorFiscalYear = (currentDay?: string | Date): dayjs.Dayjs =>
  (currentDay ? dayjs(currentDay) : dayjs()).date(15).month(3).endOf('day');

export const depositAccountConfig = (
  currentDay?: string | Date,
): DepositAccountConfigInterface => ({
  USA_INH_IRA: { endOfPriorFiscalYear: iraEndOfPriorFiscalYear(currentDay) },
  USA_INH_RT_IRA: { endOfPriorFiscalYear: iraEndOfPriorFiscalYear(currentDay) },
  USA_IRA: { endOfPriorFiscalYear: iraEndOfPriorFiscalYear(currentDay) },
  USA_RO_IRA: { endOfPriorFiscalYear: iraEndOfPriorFiscalYear(currentDay) },
  USA_RT_IRA: { endOfPriorFiscalYear: iraEndOfPriorFiscalYear(currentDay) },
  USA_SEP_IRA: { endOfPriorFiscalYear: iraEndOfPriorFiscalYear(currentDay) },
});

export const showFiscalYearBasedOnAccountType = (accountType?: string) => {
  const config =
    depositAccountConfig()[
      accountType as keyof ReturnType<typeof depositAccountConfig>
    ];
  if (config) return true;
  return false;
};

export const FETCH_TOTAL_TRANSFERS = gql`
  query fetchTransfers($input: FetchTransfersInput!) {
    fetchTransfers(input: $input) {
      totalCount
    }
  }
`;

export const FETCH_USER_LOCALIZATION = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        countryOfTaxResidence
        physicalAddress {
          country
        }
      }
    }
  }
`;

export const FETCH_SUBACCOUNT_CURRENCY_STATS = gql`
  query fetchSubAccountCurrencyStats(
    $subAccountId: ObjectID!
    $currency: StatisticsCurrencyTypes!
  ) {
    fetchSubAccount(subAccountId: $subAccountId) {
      subAccount {
        id
        statistics(input: { currency: $currency }) {
          availableFundsToWithdrawCents
        }
      }
    }
  }
`;

export const DepositForm = ({
  options,
  onNext,
  dummyDeposit,
  setDummyDeposit,
  workflowCompletion,
}: {
  options: any;
  onNext: any;
  dummyDeposit?: any;
  setDummyDeposit?: any;
  stepLoading?: boolean;
  workflowCompletion?: WorkflowCompletion;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();
  const { userId: paramsUserId } = useParams();
  const canWriteOverrideTransferRestrictions = usePermission(
    'write:override_transfer_restrictions',
  );
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');
  const { enqueueSnackbar } = useSnackbar();
  const { activeEntity, activeOrganization, userContext, custodianConnection } =
    useContext(UserContext);

  const {
    applicableData,
    updateContextWithFields,
    saveTemporaryDeposit,
    mode,
  } = useDepositWorkflowData(dummyDeposit, setDummyDeposit, workflowCompletion);

  // Seed `applicableData.type` from `options.defaultTransferType` on entry
  // when DEPOSIT_TYPE is not in the workflow. The `applicableData?.type`
  // guard makes this a no-op when DEPOSIT_TYPE precedes the form (type
  // already set) or when editing an existing deposit (type hydrated from
  // the deposit).
  useEffect(() => {
    if (mode === 'edit') return;
    if (applicableData?.type) return;
    const fallback = options?.defaultTransferType;
    if (!fallback) return;
    updateContextWithFields({ type: fallback });
  }, []);

  const [focused, setFocused] = useState<string[]>([]);
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const shouldDefaultToFutureDate = (() => {
    if (!options?.allowOneTimeTransfersFutureDate) return false;
    if (applicableData.type !== 'EFT') return false;
    if (applicableData.scheduledDate) return true;
    if (options?.transferDelay?.enabled) return true;
    if (
      options?.allowOneTimeTransfersFutureDate &&
      !options?.allowOneTimeTransfersNow
    )
      return true;
    return false;
  })();

  const [starting, setStarting] = useState<'now' | 'futureDate'>(
    applicableData.schedule === Frequency.OneTime && shouldDefaultToFutureDate
      ? 'futureDate'
      : 'now',
  );
  useEffect(() => {
    if (shouldDefaultToFutureDate) {
      setStarting('futureDate');
    } else {
      setStarting('now');
    }
  }, [shouldDefaultToFutureDate]);

  const [totalTransfers, setTotalTransfers] = useState<number | null>(null);
  const [dateHasChanged, setDateHasChanged] = useState(false);
  const [validateErrorAmounts, setValidateErrorAmounts] = useState({
    hasError: false,
    errorMessage: '',
  });
  const [scheduleError, setScheduleError] = useState<string>('');
  const [currencyBalance, setCurrencyBalance] = useState<{
    availableCents: number;
    currency: string;
  } | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Prefer sub-account owner over activeEntity when the route has no :userId (household nav).
  const userIdFromSubAccount = applicableData?.subAccount?.account?.user?.id;
  const userId = paramsUserId ?? userIdFromSubAccount ?? activeEntity?.id;
  // Skip fetchBankAccounts until :userId or sub-account when we would otherwise use only activeEntity.
  const bankAccountsQueryUserId = paramsUserId
    ? (userId ?? '')
    : applicableData?.subAccount?.id
      ? (userId ?? '')
      : '';

  const overrideTransferRestrictions = canWriteOverrideTransferRestrictions;
  const { data: localizationData } = useQuery(FETCH_USER_LOCALIZATION, {
    variables: { userId },
    skip: !userId,
  });
  const usTransferFlowEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.UsTransferFlow,
  );
  const [fetchTotal] = useLazyQuery(FETCH_TOTAL_TRANSFERS);

  const enableInKind = useOrganizationFeatureFlag(
    FeatureFlagTypes.InKindTransfers,
  );
  const enabledManualInstitutionCreation = useOrganizationFeatureFlag(
    FeatureFlagTypes.AddInstitutionsManuallyInUce,
  );
  const enableMultiCurrencyDepositTransfers = useOrganizationFeatureFlag(
    FeatureFlagTypes.MultiCurrencyDepositTransfers,
  );
  const weekdaysOnlyCalendar = useFeatureFlag(
    FlagsmithFlags.weekdays_only_calendar,
  );
  const isMultiCurrencyInternalTransfer = (type?: string): boolean => {
    const transferType = type ?? applicableData?.type;
    return (
      enableMultiCurrencyDepositTransfers &&
      transferType === 'INTERNAL_TRANSFER'
    );
  };

  const { data: subAccountData } = useQuery(FETCH_SUBACCOUNT_CURRENCY_STATS, {
    variables: {
      subAccountId: applicableData?.fromSubAccount?.id,
      currency: applicableData?.currency,
    },
    skip:
      !isMultiCurrencyInternalTransfer() ||
      !applicableData?.fromSubAccount?.id ||
      !applicableData?.currency,
  });

  useEffect(() => {
    if (subAccountData?.fetchSubAccount?.subAccount?.statistics) {
      const stats = subAccountData.fetchSubAccount.subAccount.statistics;
      const availableCents = stats.availableFundsToWithdrawCents || 0;

      setCurrencyBalance({
        availableCents,
        currency: applicableData?.currency || '',
      });
    } else if (applicableData?.currency) {
      setCurrencyBalance(null);
    }
  }, [
    subAccountData,
    applicableData?.currency,
    applicableData?.fromSubAccount?.id,
  ]);

  const scheduledDateForConfig = applicableData?.scheduledDate;
  const applicableAccountType = applicableData?.subAccount?.account
    ?.type as keyof ReturnType<typeof depositAccountConfig>;

  const depositConfig = depositAccountConfig(scheduledDateForConfig)[
    applicableAccountType
  ];
  const isCurrentContributionYearEnabled = depositConfig
    ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
    : false;

  const generateContributionYearMenuItems = () => {
    const currentScheduleDate = applicableData?.scheduledDate;
    if (applicableData.schedule === 'ONE_TIME' && starting === 'now') {
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs().isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs().isBefore(depositConfig.endOfPriorFiscalYear) ||
          dayjs().isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid='fiscal-year-select-last-year'
            value={currentYear - 1}
          >
            {currentYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid='fiscal-year-select-current-year'
            value={currentYear}
          >
            {currentYear}
          </MenuItem>
        ),
      ];
    }
    if (applicableData.schedule === 'ONE_TIME' && starting === 'futureDate') {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      const isCurrentFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isAfter(depositConfig.endOfPriorFiscalYear)
        : false;
      const isPreviousFiscalYearEnabled = depositConfig
        ? dayjs(currentScheduleDate).isBefore(
            depositConfig.endOfPriorFiscalYear,
          ) ||
          dayjs(currentScheduleDate).isSame(depositConfig.endOfPriorFiscalYear)
        : false;
      return [
        isPreviousFiscalYearEnabled && (
          <MenuItem
            data-testid='fiscal-year-select-last-year'
            value={currentSelectedYear - 1}
          >
            {currentSelectedYear - 1}
          </MenuItem>
        ),
        (isCurrentFiscalYearEnabled || isPreviousFiscalYearEnabled) && (
          <MenuItem
            data-testid='fiscal-year-select-current-year'
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }
    if (applicableData.schedule !== 'ONE_TIME') {
      const currentSelectedYear = dayjs(currentScheduleDate).year();
      return [
        currentSelectedYear && (
          <MenuItem
            data-testid='fiscal-year-select-current-year'
            value={currentSelectedYear}
          >
            {currentSelectedYear}
          </MenuItem>
        ),
      ];
    }

    return [];
  };

  const minRecurringDeposit = activeOrganization.minRecurringDepositCents;
  const minAmountDeposit = activeOrganization.minDepositCents;
  const minInitialDeposit = activeOrganization.minInitialDepositCents;

  const hyperLinkColor = theme.color.negativeOutline;
  const supportUrl =
    translateBackendString(userContext.organization?.translatedSupportUrl) ||
    DEFAULT_SUPPORT_URL;
  const currentYear = dayjs().year();

  const transferDate = applicableData?.scheduledDate;
  const delayDays = options?.transferDelay?.number;
  const transferCountry =
    applicableData?.bankAccount?.bankAccountTransitPhysicalAddress?.country ||
    localizationData?.fetchUser?.user?.physicalAddress?.country ||
    localizationData?.fetchUser?.user?.countryOfTaxResidence ||
    activeOrganization.applicableLocalization.countries[0];

  const participantNumberEnabled: boolean =
    usTransferFlowEnabled &&
    (applicableData.subAccount?.account?.type?.includes('USA_') ?? false);

  const clearValidateErrorAmountsState = () =>
    setValidateErrorAmounts({ hasError: false, errorMessage: '' });

  const handleTotalTransfers = async (subAccount: { id: string }) => {
    const { data } = await fetchTotal({
      variables: { input: { filter: { subAccountId: subAccount.id } } },
    });
    setTotalTransfers(data.fetchTransfers?.totalCount);
    checkValidationErrors(data.fetchTransfers?.totalCount);
  };

  const isInitiatedByRepresentative = () =>
    canWriteTransferOboClients && !canWriteTransferBasic;

  const confirmTransferDelay = useCallback(
    async ({
      scheduledDate,
      days,
      country,
    }: {
      scheduledDate?: Date | string;
      days: number | undefined;
      country: string;
    }) => {
      const today = new Date();
      const minTransferDate = await addBusinessDays(today, days ?? 0, country);

      return {
        dateIsAfter:
          dayjs(scheduledDate).isSame(minTransferDate, 'day') ||
          dayjs(scheduledDate).isAfter(minTransferDate),
        minTransferDate: dayjs(minTransferDate).format('MM/DD/YYYY'),
      };
    },
    [],
  );

  const [transferDelayCheck, setTransferDelayCheck] = useState<{
    dateIsAfter: boolean;
    minTransferDate: string;
  } | null>(null);
  const [scheduledDateIsBusinessDay, setScheduledDateIsBusinessDay] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [delayResult, isBd] = await Promise.all([
        confirmTransferDelay({
          scheduledDate: transferDate,
          days: delayDays,
          country: transferCountry,
        }),
        isBusinessDayAndAllowWeekend({
          date: dayjs(transferDate).utc().toDate(),
          country: transferCountry,
        }),
      ]);
      if (!cancelled) {
        setTransferDelayCheck(delayResult);
        setScheduledDateIsBusinessDay(isBd);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transferDate, delayDays, transferCountry, confirmTransferDelay]);

  const isEft = applicableData?.type === 'EFT';
  const isScheduled = starting !== 'now';
  const invalidTransferDelayCheck =
    isEft &&
    isScheduled &&
    options?.transferDelay?.enabled &&
    (!transferDelayCheck ||
      !transferDelayCheck.dateIsAfter ||
      scheduledDateIsBusinessDay === false ||
      scheduledDateIsBusinessDay === null);

  const scheduledDateError =
    (!transferDate &&
      options?.scheduledDate?.required !== FieldRequiredType.NOT_REQUIRED) ||
    scheduledDateIsBusinessDay === false ||
    (options?.transferDelay?.enabled &&
      !!transferDelayCheck &&
      !transferDelayCheck.dateIsAfter);

  const scheduledDateErrorMessage = () => {
    if (
      options?.transferDelay?.enabled &&
      transferDelayCheck &&
      !transferDelayCheck.dateIsAfter
    ) {
      const date = dayjs(transferDelayCheck.minTransferDate).format(
        'MM/DD/YYYY',
      );
      return t('bankAccountTransferCreation.transferDelayError', { date });
    }
    return t('bankAccountTransferCreation.enterAValidDate');
  };

  const checkValidationErrors = (transfersCount: number | null) => {
    clearValidateErrorAmountsState();
    setScheduleError('');

    const amountCents = applicableData?.amountCents || 0;

    if (amountCents <= 0) {
      setValidateErrorAmounts({
        hasError: true,
        errorMessage: t('components:transferModal.depositGreaterThanZero'),
      });
      return;
    }

    if (applicableData?.type !== 'EFT') return;

    const schedule = applicableData?.schedule;

    if (!schedule && options?.schedule?.enabled) {
      setScheduleError(t('components:transferModal.selectScheduleFirst'));
      return;
    }

    const effectiveSchedule = schedule || 'ONE_TIME';
    const isRecurringDeposit = effectiveSchedule !== 'ONE_TIME';

    if (isRecurringDeposit) {
      if (minRecurringDeposit && amountCents < minRecurringDeposit) {
        const errorMessage = `${t('components:transferModal.minRecurringDepositAmount')} $${minRecurringDeposit / 100}`;
        setValidateErrorAmounts({ hasError: true, errorMessage });
      }
      return;
    }

    if (isNull(transfersCount)) return;

    const isFirstDeposit = transfersCount === 0;

    if (
      isFirstDeposit &&
      minInitialDeposit &&
      amountCents < minInitialDeposit
    ) {
      const errorMessage = `${t('components:transferModal.minInitialDeposit')} $${minInitialDeposit / 100}`;
      setValidateErrorAmounts({ hasError: true, errorMessage });
      return;
    }

    if (!isFirstDeposit && minAmountDeposit && amountCents < minAmountDeposit) {
      const errorMessage = `${t('components:transferModal.minAmountDeposit')} $${minAmountDeposit / 100}`;
      setValidateErrorAmounts({ hasError: true, errorMessage });
      return;
    }
  };

  useEffect(() => {
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
  }, [applicableData?.amountCents, applicableData?.schedule]);

  useEffect(() => {
    const update = async () => {
      if (applicableData?.subAccount?.id) {
        await handleTotalTransfers({
          id: applicableData.subAccount.id,
          ...applicableData.subAccount,
        });
      }
    };
    update();
  }, [applicableData?.subAccount]);

  const defaultScheduleOption =
    options?.editDefaultScheduleFrequency && options?.defaultScheduleOption
      ? options?.defaultScheduleOption
      : undefined;
  const isNotComingFromSubAccount =
    options?.subAccount?.enabled &&
    applicableData.objectType !== PageObjectType.SUB_ACCOUNT;
  const disabled = disableForm;

  const handleValue = (value: Partial<typeof applicableData>) =>
    updateContextWithFields(value);

  const handleDateChange = async (date: any) => {
    const formattedDate = dayjs(date?.toString()).format('YYYY-MM-DD');
    setDateHasChanged(true);
    const update = { scheduledDate: formattedDate } as Partial<
      typeof applicableData
    >;

    const newDelayCheck = await confirmTransferDelay({
      scheduledDate: dayjs(date).toDate(),
      days: delayDays,
      country: transferCountry,
    });

    const isBeforeToday = dayjs(date).isBefore(dayjs(), 'day');
    const failsTransferDelay =
      options?.transferDelay?.enabled && !newDelayCheck.dateIsAfter;

    if (isBeforeToday || failsTransferDelay) {
      update.scheduledDate = undefined;
    }
    await handleValue(update);
  };

  useEffect(() => {
    if (applicableData?.schedule !== 'ONE_TIME') {
      handleValue({
        fiscalYear: undefined,
        schedule: applicableData?.schedule ?? defaultScheduleOption,
      });
    }
    if (applicableData.schedule === 'ONE_TIME' && starting === 'now') {
      handleValue({ scheduledDate: undefined });
    }
    const type = applicableData?.type;
    if (type && ['EXTERNAL_TRANSFER', 'INTERNAL_TRANSFER'].includes(type)) {
      // removing validations scheduledDate, schedule from EXTERNAL_TRANSFER and INTERNAL_TRANSFER
      handleValue({
        scheduledDate: undefined,
        schedule: Frequency.OneTime,
      });
    }
    if (isCurrentContributionYearEnabled) {
      handleValue({ fiscalYear: currentYear });
    }
  }, [applicableData?.schedule, starting, options]);

  const getOriginOfAccount = (
    accountType?: string,
  ): CountryCodes | undefined => {
    if (!accountType || accountType === '') return undefined;
    if (accountType.includes('USA_')) return CountryCodes.US;
    if (accountType.includes('UK_')) return CountryCodes.GB;
    return CountryCodes.CA;
  };

  const originAccountType = getOriginOfAccount(
    applicableData.subAccount?.account?.type,
  );

  const getAvailableCurrenciesForTransfer = () => {
    if (
      !applicableData?.fromSubAccount?.account ||
      !applicableData?.subAccount?.account
    ) {
      return [];
    }

    const fromCurrencies = applicableData.fromSubAccount.account
      .availableCurrencies || [
      applicableData.fromSubAccount.account.baseCurrency || Currencies.Cad,
    ];
    const toCurrencies = applicableData.subAccount.account
      .availableCurrencies || [
      applicableData.subAccount.account.baseCurrency || Currencies.Cad,
    ];

    return fromCurrencies.filter((currency) => toCurrencies.includes(currency));
  };

  const getSubAccountCurrencies = (subAccount: SubAccount): string[] => {
    if (!subAccount?.account) return [];
    return (
      subAccount.account.availableCurrencies || [
        subAccount.account.baseCurrency || Currencies.Cad,
      ]
    );
  };

  const filterSubAccountsByCurrency = (
    subAccount: SubAccount,
    selectedSubAccount: Partial<SubAccount> | undefined,
  ): boolean => {
    if (!enableMultiCurrencyDepositTransfers || !selectedSubAccount?.account) {
      return true;
    }

    const selectedCurrencies = getSubAccountCurrencies(
      selectedSubAccount as SubAccount,
    );
    const subAccountCurrencies = getSubAccountCurrencies(subAccount);

    return selectedCurrencies.some((currency) =>
      subAccountCurrencies.includes(currency),
    );
  };

  useEffect(() => {
    const fromAccountType = applicableData.fromSubAccount?.account?.type;
    const toAccountType = applicableData.subAccount?.account?.type;
    const isFHSATransfer =
      fromAccountType === AccountTypes.FHSA ||
      toAccountType === AccountTypes.FHSA;

    const isLockedOrIncomeAccount = [
      ...LOCKED_IN_ACCOUNTS,
      ...INCOME_FUND_ACCOUNTS,
    ].some((acc) => acc === fromAccountType || acc === toAccountType);

    if (
      applicableData?.type !== 'INTERNAL_TRANSFER' ||
      !isFHSATransfer ||
      isLockedOrIncomeAccount
    )
      return;

    const isInvalidTransferFromFHSA =
      fromAccountType === AccountTypes.FHSA &&
      toAccountType &&
      !ACCOUNTS_FOR_TRANSFER_FROM_FHSA.includes(toAccountType);
    const isInvalidTransferToFHSA =
      toAccountType === AccountTypes.FHSA &&
      fromAccountType &&
      !ACCOUNTS_FOR_TRANSFER_TO_FHSA.includes(fromAccountType);

    const isFormDisabled =
      !overrideTransferRestrictions &&
      (isInvalidTransferFromFHSA || isInvalidTransferToFHSA || false);
    setDisableForm(isFormDisabled);
  }, [applicableData]);

  useEffect(() => {
    if (
      !isMultiCurrencyInternalTransfer() ||
      !applicableData?.fromSubAccount?.id ||
      !applicableData?.subAccount?.id
    ) {
      return;
    }

    const availableCurrencies = getAvailableCurrenciesForTransfer();

    if (
      availableCurrencies.length === 1 &&
      availableCurrencies[0] !== applicableData?.currency
    ) {
      handleValue({ currency: availableCurrencies[0] });
    }
  }, [
    enableMultiCurrencyDepositTransfers,
    applicableData?.fromSubAccount?.id,
    applicableData?.subAccount?.id,
    applicableData?.type,
  ]);

  const submit = async () => {
    setIsSubmitted(true);

    // If no deposit type is selected, allow user to proceed without saving data
    if (!applicableData?.type) {
      enqueueSnackbar(t('noDepositTypeSelectedWarning'), {
        variant: 'warning',
      });
      await onNext();
      return;
    }

    const fields = invalidFields(
      {
        ...options,
        bankAccount: {
          ...options.bankAccount,
          requiredIf: (data: any) => data?.type === 'EFT',
        },
        fromSubAccount: {
          ...options.fromSubAccount,
          requiredIf: (data: any) => data?.type === 'INTERNAL_TRANSFER',
        },
        institution: {
          ...options.institution,
          requiredIf: (data: any) => data?.type === 'EXTERNAL_TRANSFER',
        },
        accountNumber: {
          ...options.accountNumber,
          requiredIf: (data: any) => data?.type === 'EXTERNAL_TRANSFER',
        },
        accountType: {
          ...options.accountType,
          requiredIf: (data: any) => data?.type === 'EXTERNAL_TRANSFER',
        },
        transferType: {
          ...options.transferType,
          requiredIf: (data: any) => data?.type === 'EXTERNAL_TRANSFER',
        },
        scheduledDate: {
          ...options.scheduledDate,
          requiredIf: (data: any) =>
            data?.schedule !== 'ONE_TIME' || starting === 'futureDate',
        },
        ...(options.holdings
          ? {
              holdings: {
                ...options.holdings,
                requiredIf: (data: any) => data?.type === 'EFT',
                validate: (data: any) =>
                  !data?.holdings?.financialProduct?.cusip &&
                  Object.keys(data?.holdings || {}).length === 0,
              },
            }
          : {}),
        participantNumber:
          participantNumberEnabled &&
          applicableData?.type === 'EXTERNAL_TRANSFER'
            ? {
                enabled: true,
                required: FieldRequiredType.ERROR,
                requiredIf: (data: any) =>
                  data?.type === 'EXTERNAL_TRANSFER' &&
                  participantNumberEnabled,
              }
            : {},
        currency: isMultiCurrencyInternalTransfer()
          ? {
              enabled: true,
              required: FieldRequiredType.ERROR,
              requiredIf: (data: any) =>
                isMultiCurrencyInternalTransfer(data?.type) &&
                getAvailableCurrenciesForTransfer().length > 0,
            }
          : {},
      },
      applicableData,
    );

    if (isNull(totalTransfers) && applicableData?.subAccount?.id) {
      await handleTotalTransfers({
        id: applicableData.subAccount.id,
        ...applicableData.subAccount,
      });
    }
    if (!isUndefined(applicableData?.amountCents)) {
      checkValidationErrors(totalTransfers);
    }
    if (validateErrorAmounts.hasError) {
      fields.push('amountCents');
    }

    if (
      participantNumberEnabled &&
      applicableData?.type === 'EXTERNAL_TRANSFER'
    ) {
      const participantNumberValue = applicableData?.participantNumber ?? '';
      if (
        participantNumberValue.length > 0 &&
        participantNumberValue.length !== 4
      ) {
        fields.push('participantNumber');
      }
    }

    if (
      isMultiCurrencyInternalTransfer() &&
      applicableData?.currency &&
      currencyBalance &&
      (applicableData?.amountCents || 0) > currencyBalance.availableCents
    ) {
      enqueueSnackbar(
        t('components:transferModal.insufficientCurrencyBalance', {
          currency: currencyBalance.currency,
          available: formatMoneyValue(
            currencyBalance.availableCents,
            currencySymbol[
              currencyBalance.currency as keyof typeof currencySymbol
            ] || '$',
          ),
        }),
        { variant: 'error' },
      );
      fields.push('amountCents');
    }

    if (fields.length > 0 || invalidTransferDelayCheck) {
      setFocused([...fields]);
      return;
    }
    const transferValidation: ValidateTransferResponse = validateTransfer({
      accountTo: applicableData?.subAccount?.account?.type ?? '',
      transferType:
        applicableData?.type === 'EFT'
          ? 'electronicFundsTransferIn'
          : applicableData?.type === 'EXTERNAL_TRANSFER'
            ? 'externalTransfer'
            : 'internalTransferTo',
      accountFrom: applicableData?.fromSubAccount?.account?.type,
      canOverrideTransferRestrictions: canWriteOverrideTransferRestrictions,
    });

    if (!transferValidation.isValid) {
      enqueueSnackbar(transferValidation.message, { variant: 'error' });
      return;
    }
    if (fields.length === 0) {
      await saveTemporaryDeposit();
      await onNext();
    }
  };

  const handleEftValidation = (e: any) => {
    const isFormDisabled =
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        e.account.type,
      ) && applicableData?.type === 'EFT';
    setDisableForm(isFormDisabled);
  };

  const handleInternalTransferFromValidation = (e: any) => {
    const isFormDisabledForAccount =
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        e.account.type,
      ) && applicableData?.type === 'INTERNAL_TRANSFER';
    const isFormDisabledForSubAccount =
      applicableData?.subAccount?.account?.type &&
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        applicableData?.subAccount?.account?.type,
      );

    const isFormDisabled =
      isFormDisabledForAccount || isFormDisabledForSubAccount || false;
    setDisableForm(isFormDisabled);
  };

  const handleInternalTransferToValidation = (e: any) => {
    const isFormDisabledForAccount =
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        e.account.type,
      ) && applicableData?.type === 'INTERNAL_TRANSFER';
    const isFormDisabledForSubAccount =
      applicableData?.fromSubAccount?.account?.type &&
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        applicableData?.fromSubAccount?.account?.type,
      );

    const isFormDisabled =
      isFormDisabledForAccount || isFormDisabledForSubAccount || false;
    setDisableForm(isFormDisabled);
  };

  const getErrorBanner = () => {
    const fromAccountType = applicableData?.fromSubAccount?.account?.type;
    const toAccountType = applicableData?.subAccount?.account?.type;

    if (
      toAccountType &&
      LOCKED_IN_ACCOUNTS.includes(toAccountType) &&
      applicableData?.type === 'EFT'
    ) {
      return (
        <TransferErrorBanner
          showHeader={false}
          theme={theme}
          i18nKey='transfer:depositEftLockedInAccountError'
          supportUrl={supportUrl}
          linkSx={{ color: hyperLinkColor }}
        />
      );
    }
    if (
      toAccountType &&
      INCOME_FUND_ACCOUNTS.includes(toAccountType) &&
      applicableData?.type === 'EFT'
    ) {
      return (
        <TransferErrorBanner
          showHeader={false}
          theme={theme}
          i18nKey='transfer:depositEftIncomeFundsAccountError'
          supportUrl={supportUrl}
          linkSx={{ color: hyperLinkColor }}
        />
      );
    }
    if (
      fromAccountType &&
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        fromAccountType,
      ) &&
      applicableData?.type === 'INTERNAL_TRANSFER'
    ) {
      return (
        <TransferErrorBanner
          showHeader={false}
          theme={theme}
          i18nKey='transfer:depositInternalTransferFromAccountError'
          supportUrl={supportUrl}
          linkSx={{ color: hyperLinkColor }}
        />
      );
    }
    if (
      toAccountType &&
      [...LOCKED_IN_ACCOUNTS, ...INCOME_FUND_ACCOUNTS].includes(
        toAccountType,
      ) &&
      applicableData?.type === 'INTERNAL_TRANSFER'
    ) {
      return (
        <TransferErrorBanner
          showHeader={false}
          theme={theme}
          i18nKey='transfer:depositInternalTransferToAccountError'
          supportUrl={supportUrl}
          linkSx={{ color: hyperLinkColor }}
        />
      );
    }

    const isInalidTransferFromFHSA =
      fromAccountType === AccountTypes.FHSA &&
      toAccountType &&
      !ACCOUNTS_FOR_TRANSFER_FROM_FHSA.includes(toAccountType);
    const isInvalidTransferToFHSA =
      toAccountType === AccountTypes.FHSA &&
      fromAccountType &&
      !ACCOUNTS_FOR_TRANSFER_TO_FHSA.includes(fromAccountType);
    const hasInvalidFHSATransfer =
      isInalidTransferFromFHSA || isInvalidTransferToFHSA;

    if (
      !overrideTransferRestrictions &&
      hasInvalidFHSATransfer &&
      applicableData?.type === 'INTERNAL_TRANSFER'
    ) {
      return (
        <TransferErrorBanner
          showHeader={false}
          theme={theme}
          i18nKey='transfer:depositFhsaInternalTransferAccountError'
          supportUrl={supportUrl}
          linkSx={{ color: hyperLinkColor }}
        />
      );
    }

    return null;
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled,
        dataTestId: 'deposit-form-continue-button',
      }}
    >
      <Typography
        variant='displayLarge'
        sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
      >
        {translateBackendString(options?.title)}
      </Typography>
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} linkTarget={'_blank'}>
            {translateBackendString(options?.subtitle)}
          </ReactMarkdown>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}
      {!applicableData?.type && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {t('noDepositTypeSelected')}
        </Alert>
      )}
      {applicableData?.type === 'EFT' && (
        <>
          {enableMultiCurrencyDepositTransfers ? (
            <>
              {options?.subAccount?.enabled &&
                applicableData.objectType !== PageObjectType.SUB_ACCOUNT && (
                  <SubAccountSelect
                    omitAny
                    sx={{ mb: 2 }}
                    filter={objectFilter(
                      applicableData?.objectType,
                      applicableData?.objectId ?? userId,
                    )}
                    dataTestId='deposit-form-subaccount-select'
                    label={translateBackendString(options?.subAccount?.label)}
                    onSubAccountSelect={async (e: any) => {
                      handleEftValidation(e);
                      handleValue({ subAccount: e, bankAccount: undefined });
                      await handleTotalTransfers(e);
                    }}
                    selectedSubAccount={applicableData?.subAccount}
                    onBlur={() => setFocused([...focused, 'subAccount'])}
                    error={
                      !applicableData?.subAccount?.id &&
                      focused.includes('subAccount') &&
                      options?.subAccount?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    showCurrencyAvatar={true}
                    displayCustodianAccountNumber={
                      options.displayCustodianAccountNumber
                    }
                    selectFirstSubAccountOnly={
                      options.selectFirstSubAccountOnly
                    }
                  />
                )}
              {options?.bankAccount?.enabled && (
                <BankAccountSelectField
                  key={`bank-account-select-${applicableData?.subAccount?.id}`}
                  userId={bankAccountsQueryUserId}
                  sx={{ mb: 2 }}
                  disabled={!bankAccountsQueryUserId}
                  setBankAccount={(e: any) => handleValue({ bankAccount: e })}
                  bankAccount={applicableData?.bankAccount || null}
                  account={applicableData?.subAccount?.account}
                  onBlur={() => setFocused([...focused, 'bankAccount'])}
                  error={
                    !applicableData?.bankAccount?.id &&
                    focused.includes('bankAccount') &&
                    options?.bankAccount?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  options={options}
                  showSelectedIcon={true}
                />
              )}
            </>
          ) : (
            <>
              {options?.subAccount?.enabled &&
                applicableData?.objectType !== PageObjectType.SUB_ACCOUNT && (
                  <SubAccountSelect
                    omitAny
                    sx={{ mb: 2 }}
                    filter={objectFilter(
                      applicableData?.objectType,
                      applicableData?.objectId ?? userId,
                    )}
                    dataTestId='deposit-form-subaccount-select'
                    label={translateBackendString(options?.subAccount?.label)}
                    onSubAccountSelect={async (e: any) => {
                      handleEftValidation(e);
                      handleValue({ subAccount: e, bankAccount: undefined });
                      await handleTotalTransfers(e);
                    }}
                    selectedSubAccount={applicableData?.subAccount}
                    onBlur={() => setFocused([...focused, 'subAccount'])}
                    error={
                      !applicableData?.subAccount?.id &&
                      focused.includes('subAccount') &&
                      options?.subAccount?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    displayCustodianAccountNumber={
                      options.displayCustodianAccountNumber
                    }
                    selectFirstSubAccountOnly={
                      options.selectFirstSubAccountOnly
                    }
                  />
                )}
              {options?.bankAccount?.enabled && (
                <BankAccountSelectField
                  key={`bank-account-select-${applicableData?.subAccount?.id}`}
                  userId={bankAccountsQueryUserId}
                  sx={{ mb: 2 }}
                  disabled={!bankAccountsQueryUserId}
                  setBankAccount={(e: any) => handleValue({ bankAccount: e })}
                  bankAccount={applicableData?.bankAccount || null}
                  account={applicableData?.subAccount?.account}
                  onBlur={() => setFocused([...focused, 'bankAccount'])}
                  error={
                    !applicableData?.bankAccount?.id &&
                    focused.includes('bankAccount') &&
                    options?.bankAccount?.required !==
                      FieldRequiredType.NOT_REQUIRED
                  }
                  options={options}
                  showSelectedIcon={!!enableMultiCurrencyDepositTransfers}
                />
              )}
            </>
          )}
          {options?.holdings?.enabled && (
            <HoldingSelectField
              userId={userId ?? ''}
              sx={{ mb: 2 }}
              disabled={!applicableData?.subAccount?.account?.id}
              label={translateBackendString(options?.holdings?.label)}
              setAccountHolding={(e: any) => handleValue({ holdings: e })}
              onBlur={() => setFocused([...focused, 'holdings'])}
              selectedSubAccount={applicableData?.subAccount}
              selectedHolding={applicableData?.holdings}
              error={
                !applicableData?.holdings?.financialProduct?.cusip &&
                options?.holdings?.required !== FieldRequiredType.NOT_REQUIRED
              }
              isSubmitted={isSubmitted}
              options={options}
              holdingFilterOptions={{
                includeCash: custodianConnection?.allowCashAsDepositTarget,
              }}
            />
          )}
          {options?.fiscalYear?.enabled &&
            showFiscalYearBasedOnAccountType(
              applicableAccountType as string,
            ) && (
              <SelectField
                fullWidth
                sx={{ mb: 2 }}
                testId='fiscal-year-select'
                value={applicableData?.fiscalYear}
                label={translateBackendString(options?.fiscalYear?.label)}
                onChange={(e: any) =>
                  handleValue({ fiscalYear: e.target.value })
                }
              >
                {generateContributionYearMenuItems()}
              </SelectField>
            )}
          {options?.amountCents?.enabled && (
            <AmountField
              disabled={!applicableData?.subAccount || totalTransfers === null}
              useFormatAmountValueCents
              sx={{ mb: 2 }}
              testId='deposit-form-amount-cents'
              label={translateBackendString(options?.amountCents?.label)}
              setAmount={(e: any) =>
                handleValue({
                  amountCents: e ? dollars2cents(e, 2) : undefined,
                })
              }
              amount={applicableData?.amountCents}
              onBlur={() => setFocused([...focused, 'amountCents'])}
              error={
                (!applicableData?.amountCents &&
                  focused.includes('amountCents') &&
                  options?.amountCents?.required !==
                    FieldRequiredType.NOT_REQUIRED) ||
                (validateErrorAmounts.hasError &&
                  focused.includes('amountCents'))
              }
              errorText={
                validateErrorAmounts.hasError
                  ? validateErrorAmounts.errorMessage
                  : undefined
              }
              {...(enableMultiCurrencyDepositTransfers
                ? {
                    currency:
                      applicableData?.bankAccount?.currency ||
                      applicableData?.subAccount?.account?.baseCurrency ||
                      activeOrganization?.productShelfCurrencies?.[0] ||
                      Currencies.Cad,
                  }
                : {})}
            />
          )}
          {options?.schedule?.enabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2 }}
              testId='schedule-select'
              value={applicableData?.schedule ?? defaultScheduleOption}
              label={translateBackendString(options?.schedule?.label)}
              onChange={(e: any) => handleValue({ schedule: e.target.value })}
              onBlur={() => setFocused([...focused, 'schedule'])}
              error={
                !applicableData?.schedule &&
                focused.includes('schedule') &&
                options?.schedule?.required !== FieldRequiredType.NOT_REQUIRED
              }
              errorText={scheduleError}
            >
              <MenuItem data-testid='schedule-select-one-time' value='ONE_TIME'>
                {t('schedule.ONE_TIME')}
              </MenuItem>
              <MenuItem data-testid='schedule-select-weekly' value='WEEKLY'>
                {t('schedule.WEEKLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-bi-weekly'
                value='BI_WEEKLY'
              >
                {t('schedule.BI_WEEKLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-semi-monthly'
                value='SEMI_MONTHLY'
              >
                {t('schedule.SEMI_MONTHLY')}
              </MenuItem>
              <MenuItem data-testid='schedule-select-monthly' value='MONTHLY'>
                {t('schedule.MONTHLY')}
              </MenuItem>
              <MenuItem
                data-testid='schedule-select-quarterly'
                value='QUARTERLY'
              >
                {t('schedule.QUARTERLY')}
              </MenuItem>
            </SelectField>
          )}
          {options?.scheduledDate?.enabled &&
            applicableData?.schedule !== Frequency.OneTime && (
              <DateField
                fullWidth
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={handleDateChange}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={
                  options?.transferDelay?.enabled
                    ? (transferDelayCheck?.minTransferDate ??
                      dayjs().add(1, 'day'))
                    : dayjs().add(1, 'day')
                }
                shouldDisableDate={(date: any) => {
                  const dayjsDate = dayjs(date);
                  const dayOfMonth = dayjsDate.date();

                  // For SEMI_MONTHLY, only allow 1st and 15th
                  if (applicableData?.schedule === Frequency.SemiMonthly) {
                    return dayOfMonth !== 1 && dayOfMonth !== 15;
                  }

                  // For other schedules, use business day logic
                  return weekdaysOnlyCalendar
                    ? !isBusinessDaySync({
                        date: dayjsDate.utc().toDate(),
                        country: transferCountry,
                      })
                    : !isBusinessDayAndAllowWeekendSync({
                        date: dayjsDate.utc().toDate(),
                        country: transferCountry,
                      });
                }}
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage()}
                autocomplete='off'
              />
            )}
          {(options?.allowOneTimeTransfersFutureDate ||
            options?.allowOneTimeTransfersNow) &&
            applicableData?.schedule === 'ONE_TIME' && (
              <RadioGroup
                testId='deposit-form-starting'
                label={t('components:transferModal.starting')}
                sx={{ pb: 0 }}
                value={starting}
                onChange={(e: any) => {
                  setStarting(e.target.value);
                  if (e.target.value === 'futureDate') {
                    setFocused([...focused, 'scheduledDate']);
                  }
                  if (e.target.value === 'now') {
                    handleValue({ scheduledDate: undefined });
                  }
                }}
              >
                <Stack direction={'row'}>
                  {!isInitiatedByRepresentative() &&
                    options?.allowOneTimeTransfersNow && (
                      <Radio
                        testId='deposit-form-starting-now'
                        value='now'
                        label={t(
                          'components:transferModal.startingOptions.now',
                        )}
                      />
                    )}
                  {options?.allowOneTimeTransfersFutureDate && (
                    <Radio
                      testId='deposit-form-starting-future'
                      value='futureDate'
                      label={t(
                        'components:transferModal.startingOptions.futureDate',
                      )}
                    />
                  )}
                </Stack>
              </RadioGroup>
            )}
          {starting === 'futureDate' &&
            applicableData?.schedule === 'ONE_TIME' && (
              <DateField
                fullWidth
                sx={{ mb: 2 }}
                dataTestId='scheduled-date'
                onChange={handleDateChange}
                label={translateBackendString(options?.scheduledDate?.label)}
                minDate={
                  options?.transferDelay?.enabled
                    ? (transferDelayCheck?.minTransferDate ??
                      dayjs().add(1, 'day'))
                    : dayjs().add(1, 'day')
                }
                shouldDisableDate={(date: any) =>
                  weekdaysOnlyCalendar
                    ? !isBusinessDaySync({
                        date: dayjs(date).utc().toDate(),
                        country: transferCountry,
                      })
                    : !isBusinessDayAndAllowWeekendSync({
                        date: dayjs(date).utc().toDate(),
                        country: transferCountry,
                      })
                }
                value={applicableData?.scheduledDate}
                error={
                  scheduledDateError &&
                  (dateHasChanged || focused.includes('scheduledDate'))
                }
                errorText={scheduledDateErrorMessage()}
                autocomplete='off'
              />
            )}
        </>
      )}

      {options?.transferDelay?.enabled &&
        ((applicableData.schedule === 'ONE_TIME' && starting !== 'now') ||
          applicableData.schedule !== 'ONE_TIME') && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            {translateBackendString(options?.transferDelayDescription)}
          </Alert>
        )}

      {applicableData?.type === 'INTERNAL_TRANSFER' && (
        <>
          {options?.fromSubAccount?.enabled && (
            <SubAccountSelect
              omitAny
              sx={{ mb: 2 }}
              userId={userId}
              dataTestId='deposit-form-from-sub-account-select'
              label={translateBackendString(options?.fromSubAccount?.label)}
              // if this page is navigated from the subAccount page. The subAccount its coming from should have its
              // id assigned to the subAccountsToExclude, so it is not shown in the list of subAccount from, since it is
              // the subAccount money would be transferred into subAccountTo
              subAccountsToExclude={
                !isNotComingFromSubAccount && applicableData?.subAccount?.id
                  ? [applicableData?.subAccount?.id]
                  : []
              }
              filterSubAccount={(subAccount: SubAccount) =>
                filterSubAccountsByCurrency(
                  subAccount,
                  applicableData?.subAccount,
                )
              }
              onSubAccountSelect={(e: any) => {
                handleInternalTransferFromValidation(e);
                const hasMatchingCurrencies =
                  applicableData?.subAccount?.account?.availableCurrencies?.every(
                    (currency: string) =>
                      e.account.availableCurrencies?.includes(currency),
                  );

                const shouldClearTo =
                  applicableData?.subAccount?.id === e.id ||
                  (!hasMatchingCurrencies &&
                    !enableMultiCurrencyDepositTransfers);

                if (shouldClearTo) {
                  handleValue({
                    subAccount: undefined,
                    fromSubAccount: e,
                    currency: undefined,
                  });
                } else {
                  handleValue({ fromSubAccount: e, currency: undefined });
                }
              }}
              selectedSubAccount={applicableData?.fromSubAccount}
              onBlur={() => setFocused([...focused, 'fromSubAccount'])}
              error={
                !applicableData?.fromSubAccount &&
                focused.includes('fromSubAccount') &&
                options?.fromSubAccount?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
              showCurrencyAvatar={!!enableMultiCurrencyDepositTransfers}
              displayCustodianAccountNumber={
                options.displayCustodianAccountNumber
              }
              selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
            />
          )}
          {isNotComingFromSubAccount && (
            <SubAccountSelect
              omitAny
              filter={objectFilter(
                applicableData?.objectType,
                applicableData?.objectId ?? userId,
              )}
              sx={{ mb: 2 }}
              dataTestId='deposit-form-sub-account-select'
              label={translateBackendString(options?.subAccount?.label)}
              subAccountsToExclude={
                applicableData?.fromSubAccount?.id
                  ? [applicableData?.fromSubAccount?.id]
                  : []
              }
              filterSubAccount={(subAccount: SubAccount) =>
                filterSubAccountsByCurrency(
                  subAccount,
                  applicableData?.fromSubAccount,
                )
              }
              onSubAccountSelect={(e: any) => {
                handleInternalTransferToValidation(e);
                const hasMatchingCurrencies =
                  applicableData?.fromSubAccount?.account?.availableCurrencies?.every(
                    (currency: string) =>
                      e.account.availableCurrencies?.includes(currency),
                  );

                const shouldClearFrom =
                  applicableData?.fromSubAccount?.id === e.id ||
                  (!hasMatchingCurrencies &&
                    !enableMultiCurrencyDepositTransfers);

                if (shouldClearFrom) {
                  handleValue({
                    fromSubAccount: undefined,
                    subAccount: e,
                    currency: undefined,
                  });
                } else {
                  handleValue({ subAccount: e, currency: undefined });
                }
              }}
              selectedSubAccount={applicableData?.subAccount}
              onBlur={() => setFocused([...focused, 'subAccount'])}
              error={
                (!applicableData?.subAccount ||
                  Object.keys(applicableData.subAccount).length === 0) &&
                focused.includes('subAccount') &&
                options?.subAccount?.required !== FieldRequiredType.NOT_REQUIRED
              }
              showCurrencyAvatar={!!enableMultiCurrencyDepositTransfers}
              displayCustodianAccountNumber={
                options.displayCustodianAccountNumber
              }
              selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
            />
          )}
          {enableMultiCurrencyDepositTransfers &&
            applicableData?.fromSubAccount?.id &&
            applicableData?.subAccount?.id &&
            getAvailableCurrenciesForTransfer().length > 1 && (
              <SelectField
                fullWidth
                sx={{ mb: 2 }}
                testId='internal-transfer-currency-select'
                label={t('transfer:selectCurrency')}
                value={applicableData?.currency || ''}
                onChange={(e: any) => {
                  handleValue({ currency: e.target.value });
                  setFocused([...focused, 'currency']);
                }}
                error={
                  !applicableData?.currency &&
                  focused.includes('currency') &&
                  getAvailableCurrenciesForTransfer().length > 0
                }
              >
                {getAvailableCurrenciesForTransfer().map((currency) => (
                  <MenuItem
                    key={currency}
                    value={currency}
                    data-testid={`currency-option-${currency}`}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <img
                        src={getCurrencyFlag(currency as CurrencyCodes)}
                        alt={currency}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                      {currency}
                    </Box>
                  </MenuItem>
                ))}
              </SelectField>
            )}
          {options?.amountCents?.enabled && (
            <>
              <AmountField
                useFormatAmountValueCents
                sx={{ mb: 1 }}
                testId='deposit-form-amount-cents'
                label={translateBackendString(options?.amountCents?.label)}
                setAmount={(e: any) =>
                  handleValue({
                    amountCents: e ? dollars2cents(e, 2) : undefined,
                  })
                }
                amount={applicableData?.amountCents}
                onBlur={() => setFocused([...focused, 'amountCents'])}
                error={
                  (!applicableData?.amountCents &&
                    focused.includes('amountCents') &&
                    options?.amountCents?.required !==
                      FieldRequiredType.NOT_REQUIRED) ||
                  (validateErrorAmounts.hasError &&
                    focused.includes('amountCents'))
                }
                errorText={
                  validateErrorAmounts.hasError
                    ? validateErrorAmounts.errorMessage
                    : undefined
                }
                {...(enableMultiCurrencyDepositTransfers &&
                applicableData?.currency
                  ? { currency: applicableData.currency }
                  : {})}
              />
              {enableMultiCurrencyDepositTransfers &&
                applicableData?.currency &&
                currencyBalance && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 2,
                      mt: 0.5,
                    }}
                    data-testid='currency-balance-display'
                  >
                    <InfoOutlinedIcon
                      sx={{
                        fontSize: 16,
                        color:
                          (applicableData?.amountCents || 0) >
                          currencyBalance.availableCents
                            ? 'error.main'
                            : 'info.main',
                      }}
                    />
                    <Typography variant='labelLarge' color='textSecondary'>
                      {t('transfer:availableCurrencyBalance', {
                        currency: currencyBalance.currency,
                      })}
                    </Typography>
                    <Typography
                      variant='bodyLarge'
                      color={
                        (applicableData?.amountCents || 0) >
                        currencyBalance.availableCents
                          ? 'error'
                          : 'textPrimary'
                      }
                      sx={{ fontWeight: 600 }}
                    >
                      {formatMoneyValue(
                        currencyBalance.availableCents,
                        currencySymbol[
                          currencyBalance.currency as keyof typeof currencySymbol
                        ] || '$',
                      )}
                    </Typography>
                  </Box>
                )}
            </>
          )}
          {!enableMultiCurrencyDepositTransfers &&
            (applicableData?.fromSubAccount?.statistics?.marketValueCents ??
              0) > 0 &&
            (applicableData?.amountCents || 0) >
              (applicableData?.fromSubAccount?.statistics?.marketValueCents ||
                0) *
                0.97 && (
              <Typography
                variant='labelSmall'
                component='div'
                color='error'
                sx={{ mt: 1 }}
              >
                {t('depositReview.amountExceeds')}
              </Typography>
            )}

          {showFiscalYearBasedOnAccountType(
            applicableAccountType as string,
          ) && (
            <SelectField
              fullWidth
              sx={{ mb: 2 }}
              testId='fiscal-year-select'
              value={applicableData?.fiscalYear}
              label={translateBackendString(options?.fiscalYear?.label)}
              onChange={(e: any) => handleValue({ fiscalYear: e.target.value })}
            >
              {generateContributionYearMenuItems()}
            </SelectField>
          )}
        </>
      )}

      {applicableData?.type === 'EXTERNAL_TRANSFER' && (
        <>
          {options?.subAccount?.enabled &&
            applicableData.objectType !== PageObjectType.SUB_ACCOUNT && (
              <SubAccountSelect
                omitAny
                filter={objectFilter(
                  applicableData?.objectType,
                  applicableData?.objectId ?? userId,
                )}
                sx={{ mb: 2 }}
                dataTestId='deposit-form-sub-account-select'
                label={translateBackendString(options?.subAccount?.label)}
                onSubAccountSelect={(e: any) =>
                  handleValue({ subAccount: e, accountType: undefined })
                }
                selectedSubAccount={applicableData?.subAccount}
                onBlur={() => setFocused([...focused, 'subAccount'])}
                error={
                  (!applicableData?.subAccount ||
                    Object.keys(applicableData?.subAccount).length === 0) &&
                  focused.includes('subAccount') &&
                  options?.subAccount?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
                displayCustodianAccountNumber={
                  options.displayCustodianAccountNumber
                }
                selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
              />
            )}
          {options?.institution?.enabled && (
            <BankSelectField
              fullWidth
              sx={{ mb: 2 }}
              label={translateBackendString(options?.institution?.label)}
              setInstitution={(e: any) => handleValue({ institution: e })}
              institution={applicableData?.institution}
              onBlur={() => setFocused([...focused, 'institution'])}
              error={
                (!applicableData?.institution ||
                  Object.keys(applicableData.institution).length === 0) &&
                focused.includes('institution') &&
                options?.institution?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
              addInstitutionManually={enabledManualInstitutionCreation}
              originOfAccount={
                originAccountType && usTransferFlowEnabled
                  ? originAccountType
                  : undefined
              }
            />
          )}
          {participantNumberEnabled &&
            (() => {
              const participantNumberValue =
                applicableData?.participantNumber ?? '';
              const participantNumberTouched =
                focused.includes('participantNumber');
              const participantNumberMissing =
                participantNumberTouched && participantNumberValue.length === 0;
              const participantNumberInvalidLength =
                participantNumberTouched &&
                participantNumberValue.length > 0 &&
                participantNumberValue.length !== 4;

              return (
                <TextField
                  testId='participant-number-for-us-transfers'
                  infoTooltip={t('transfer:participantNumberTooltip')}
                  onChange={(e: any) => {
                    const digitsOnly = e.target.value
                      .replace(/\D/g, '')
                      .slice(0, 4);
                    handleValue({ participantNumber: digitsOnly });
                  }}
                  label={t('transfer:participantNumber')}
                  fullWidth
                  sx={{ mt: 2, mb: 2 }}
                  value={participantNumberValue}
                  inputProps={{
                    maxLength: 4,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  onBlur={() => setFocused([...focused, 'participantNumber'])}
                  error={
                    participantNumberMissing || participantNumberInvalidLength
                  }
                  errorText={
                    participantNumberInvalidLength
                      ? t('transfer:participantNumberLengthError')
                      : undefined
                  }
                />
              );
            })()}
          {options?.accountNumber?.enabled && (
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              testId='deposit-form-account-number'
              label={translateBackendString(options?.accountNumber?.label)}
              onChange={(e: any) =>
                handleValue({ accountNumber: e.target.value })
              }
              value={applicableData?.accountNumber}
              onBlur={() => setFocused([...focused, 'accountNumber'])}
              error={
                !applicableData?.accountNumber &&
                focused.includes('accountNumber') &&
                options?.accountNumber?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          )}
          {options?.accountType?.enabled &&
            (!usTransferFlowEnabled || originAccountType) && (
              <AccountTypeSelect
                size='medium'
                label={translateBackendString(options?.accountType?.label)}
                onChange={(e: any) => handleValue({ accountType: e })}
                value={applicableData?.accountType}
                onBlur={() => setFocused([...focused, 'institution'])}
                error={
                  !applicableData?.accountType &&
                  focused.includes('accountType') &&
                  options?.accountType?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
                originOfAccount={
                  originAccountType && usTransferFlowEnabled
                    ? originAccountType
                    : undefined
                }
                bypassFilteringByAvailableFeatureFlags={
                  originAccountType && usTransferFlowEnabled
                }
              />
            )}
          {options?.transferType?.enabled && !usTransferFlowEnabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2, mt: 2 }}
              testId='transfer-type-select'
              label={translateBackendString(options?.transferType?.label)}
              onChange={(e: any) =>
                handleValue({ transferType: e.target.value })
              }
              value={applicableData?.transferType}
              onBlur={() => setFocused([...focused, 'transferType'])}
              error={
                !applicableData?.transferType &&
                focused.includes('transferType') &&
                options?.transferType?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              <MenuItem
                data-testid='transfer-type-select-all-cash'
                value='ALL_IN_CASH'
              >
                {t('depositReview.ALL_IN_CASH')}
              </MenuItem>
              <MenuItem
                data-testid='transfer-type-select-partial-cash'
                value='PARTIAL_CASH'
              >
                {t('depositReview.PARTIAL_CASH')}
              </MenuItem>
              {enableInKind && (
                <MenuItem
                  data-testid='transfer-type-select-all-in-kind'
                  value='ALL_IN_KIND'
                >
                  {t('depositReview.ALL_IN_KIND')}
                </MenuItem>
              )}
            </SelectField>
          )}
          {options?.transferType?.enabled && usTransferFlowEnabled && (
            <SelectField
              fullWidth
              sx={{ mb: 2, mt: 2 }}
              testId='transfer-type-select'
              label={translateBackendString(options?.transferType?.label)}
              onChange={(e: any) =>
                handleValue({ transferType: e.target.value })
              }
              value={applicableData?.transferType}
              onBlur={() => setFocused([...focused, 'transferType'])}
              error={
                !applicableData?.transferType &&
                focused.includes('transferType') &&
                options?.transferType?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            >
              {activeOrganization?.enableFullCashTransfers && (
                <MenuItem
                  data-testid='transfer-type-select-all-cash'
                  value='ALL_IN_CASH'
                >
                  {t('depositReview.ALL_IN_CASH')}
                </MenuItem>
              )}
              {activeOrganization?.enablePartialCashTransfers && (
                <MenuItem
                  data-testid='transfer-type-select-partial-cash'
                  value='PARTIAL_CASH'
                >
                  {t('depositReview.PARTIAL_CASH')}
                </MenuItem>
              )}
              {activeOrganization?.enableAllInKindTransfers && enableInKind && (
                <MenuItem
                  data-testid='transfer-type-select-all-in-kind'
                  value='ALL_IN_KIND'
                >
                  {t('depositReview.ALL_IN_KIND')}
                </MenuItem>
              )}
            </SelectField>
          )}
          {options?.amountCents?.enabled && (
            <AmountField
              useFormatAmountValueCents
              testId='deposit-form-amount-cents'
              label={
                applicableData?.transferType === 'ALL_IN_CASH'
                  ? t('depositReview.estimatedAmountLabel')
                  : translateBackendString(options?.amountCents?.label)
              }
              setAmount={(e: any) =>
                handleValue({
                  amountCents: e ? dollars2cents(e, 2) : undefined,
                })
              }
              amount={applicableData?.amountCents}
              onBlur={() => setFocused([...focused, 'amountCents'])}
              error={
                (!applicableData?.amountCents &&
                  focused.includes('amountCents') &&
                  options?.amountCents?.required !==
                    FieldRequiredType.NOT_REQUIRED) ||
                (validateErrorAmounts.hasError &&
                  focused.includes('amountCents'))
              }
              errorText={
                validateErrorAmounts.hasError
                  ? validateErrorAmounts.errorMessage
                  : undefined
              }
            />
          )}
          {(applicableData?.fromSubAccount?.statistics?.marketValueCents ?? 0) >
            0 &&
            (applicableData?.amountCents || 0) >
              (applicableData?.fromSubAccount?.statistics?.marketValueCents ||
                0) *
                0.97 && (
              <Typography
                variant='labelSmall'
                component='div'
                color='error'
                sx={{ mt: 1 }}
              >
                {t('depositReview.amountExceeds')}
              </Typography>
            )}
        </>
      )}

      {applicableData?.schedule !== 'ONE_TIME' &&
        showFiscalYearBasedOnAccountType(
          applicableData?.subAccount?.account?.type,
        ) && (
          <Alert severity='info' data-testid='transfer-delay-alert'>
            <Typography variant='bodySmall' component='p'>
              {t('fiscalYearForRecurringContributions')}
            </Typography>
          </Alert>
        )}

      {/* Later this section will be implement as editable from configuration level */}
      {applicableData?.transferType &&
        applicableData?.type === 'EXTERNAL_TRANSFER' && (
          <Box sx={{ mt: 2, mb: 2 }}>
            {applicableData?.transferType === 'ALL_IN_CASH' && (
              <Alert variant='outlined' icon={<InfoIcon />} severity='info'>
                <Typography variant='bodySmall' component='p'>
                  {t('depositReview.allInCashMessage')}
                </Typography>
              </Alert>
            )}
            {applicableData?.transferType === 'PARTIAL_CASH' && (
              <Alert variant='outlined' icon={<InfoIcon />} severity='info'>
                <Typography variant='bodySmall' component='p'>
                  {t('depositReview.partialCashMessage')}
                </Typography>
              </Alert>
            )}
            {applicableData?.transferType === 'ALL_IN_KIND' && (
              <Alert variant='outlined' icon={<InfoIcon />} severity='info'>
                <Typography variant='bodySmall' component='p'>
                  {t('depositReview.allInKindMessage')}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

      {getErrorBanner()}
    </WorkflowCompletionForm>
  );
};

export default DepositForm;
